import { format } from 'date-fns';
import { sv } from 'date-fns/locale';
import { 
  Package, 
  User, 
  Mail, 
  Phone,
  Calendar,
  Clock,
  CreditCard, 
  Loader2,
  Printer,
  Download,
  Check,
  PlayCircle,
  XCircle,
  Banknote,
  Truck,
  MapPin,
  PhoneCall,
  MailIcon,
  HandCoins,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { ResponsiveOverlay } from '@/components/ui/ResponsiveOverlay';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Checkbox } from '@/components/ui/checkbox';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { OrderStatusBadge } from './OrderStatusBadge';
import { 
  ProducerOrder, 
  OrderStatus,
  OrderLineItem,
  useUpdateOrderStatus,
  useOrderItems,
  useMarkOrderPaid,
  formatOrderPriceWhole,
} from '@/hooks/useProducerOrders';
import { useProductCoverImages } from '@/hooks/useProductCoverImages';
import { ProductThumb } from '@/components/shop/ProductThumb';
import { useToast } from '@/hooks/use-toast';
import { useState, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useQuery } from '@tanstack/react-query';
import { formatPickupWindowLabel, type PickupWindow } from '@/hooks/usePickupWindows';
interface OrderDetailSheetProps {
  order: ProducerOrder | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// Generate CSV content for order
function generateOrderCsv(order: ProducerOrder, lineItems: OrderLineItem[]): string {
  const headers = ['order_id', 'created_at', 'product_title', 'qty', 'unit_price', 'total'];
  const rows = lineItems.map(item => [
    order.order_number || order.id.slice(0, 8),
    format(new Date(order.created_at), 'yyyy-MM-dd HH:mm'),
    item.title,
    item.quantity.toString(),
    (item.unit_amount / 100).toFixed(2),
    (item.total_amount / 100).toFixed(2),
  ]);
  
  const csvContent = [
    headers.join(','),
    ...rows.map(row => row.map(cell => `"${cell.replace(/"/g, '""')}"`).join(',')),
  ].join('\n');
  
  return csvContent;
}

// Download CSV file
function downloadCsv(content: string, filename: string) {
  const blob = new Blob(['\uFEFF' + content], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
  URL.revokeObjectURL(link.href);
}

// Print order pick list
function printPickList(order: ProducerOrder, lineItems: OrderLineItem[]) {
  const printWindow = window.open('', '_blank');
  if (!printWindow) return;

  const html = `
    <!DOCTYPE html>
    <html lang="sv">
    <head>
      <meta charset="UTF-8">
      <title>Plocklista - Order #${order.order_number || order.id.slice(0, 8)}</title>
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: system-ui, -apple-system, sans-serif; padding: 20mm; }
        h1 { font-size: 24px; margin-bottom: 8px; }
        .meta { color: #666; margin-bottom: 24px; font-size: 14px; }
        .customer { margin-bottom: 24px; padding: 12px; background: #f5f5f5; border-radius: 4px; }
        .customer h3 { font-size: 14px; margin-bottom: 8px; }
        table { width: 100%; border-collapse: collapse; margin-bottom: 24px; }
        th, td { padding: 12px 8px; text-align: left; border-bottom: 1px solid #ddd; }
        th { background: #f9f9f9; font-weight: 600; }
        .qty { font-size: 20px; font-weight: bold; text-align: center; }
        .checkbox { width: 24px; height: 24px; border: 2px solid #333; border-radius: 4px; }
        .total { font-size: 18px; font-weight: bold; text-align: right; }
        @media print {
          body { padding: 10mm; }
          .no-print { display: none; }
        }
      </style>
    </head>
    <body>
      <h1>Plocklista</h1>
      <p class="meta">
        Order #${order.order_number || order.id.slice(0, 8)} • 
        ${format(new Date(order.created_at), 'PPP HH:mm', { locale: sv })}
      </p>
      
      ${order.customer_name || order.customer_email ? `
        <div class="customer">
          <h3>Kund</h3>
          ${order.customer_name ? `<p>${order.customer_name}</p>` : ''}
          ${order.customer_email ? `<p>${order.customer_email}</p>` : ''}
        </div>
      ` : ''}
      
      <table>
        <thead>
          <tr>
            <th style="width: 40px;">✓</th>
            <th style="width: 60px;">Antal</th>
            <th>Produkt</th>
            <th style="width: 100px; text-align: right;">Pris</th>
          </tr>
        </thead>
        <tbody>
          ${lineItems.map(item => `
            <tr>
              <td><div class="checkbox"></div></td>
              <td class="qty">${item.quantity}</td>
              <td>${item.title}</td>
              <td style="text-align: right;">${Math.round(item.unit_amount / 100).toLocaleString('sv-SE')} kr</td>
            </tr>
          `).join('')}
        </tbody>
      </table>
      
      <p class="total">Totalt: ${formatOrderPriceWhole(order.total_amount, order.currency)}</p>
      
      <script>window.onload = () => window.print();</script>
    </body>
    </html>
  `;

  printWindow.document.write(html);
  printWindow.document.close();
}

const PAYMENT_METHODS = [
  { value: 'swish', label: 'Swish' },
  { value: 'cash', label: 'Kontant' },
  { value: 'invoice', label: 'Faktura' },
  { value: 'other', label: 'Annat' },
] as const;

export function OrderDetailSheet({ order, open, onOpenChange }: OrderDetailSheetProps) {
  const { toast } = useToast();
  const updateStatus = useUpdateOrderStatus();
  const markPaid = useMarkOrderPaid();
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [completedDialogOpen, setCompletedDialogOpen] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('swish');
  const [pickedItems, setPickedItems] = useState<Set<number>>(new Set());

  // Fetch line items for this order
  const { data: lineItems = [] } = useOrderItems(order?.id);

  // Batch-fetch product cover images for all line items
  const productIds = useMemo(
    () => lineItems.map(i => i.product_id).filter((id): id is string => !!id),
    [lineItems],
  );
  const { data: coverMap = {} } = useProductCoverImages(productIds, 320);

  // Fetch pickup window info if order has one
  const pickupWindowId = (order as any)?.pickup_window_id as string | null;
  const { data: pickupWindowData } = useQuery({
    queryKey: ['pickup-window', pickupWindowId],
    queryFn: async () => {
      if (!pickupWindowId) return null;
      const { data, error } = await supabase
        .from('pickup_windows' as any)
        .select('*')
        .eq('id', pickupWindowId)
        .maybeSingle();
      if (error) throw error;
      return data as unknown as PickupWindow | null;
    },
    enabled: !!pickupWindowId,
  });
  const pickupWindowLabel = pickupWindowData ? formatPickupWindowLabel(pickupWindowData) : null;

  if (!order) return null;

  const isUpdating = updateStatus.isPending;
  const isMarkingPaid = markPaid.isPending;
  const currentStatus: OrderStatus = order.status;

  const isPaid = order.payment_status === 'paid';

  const handleStatusChange = async (newStatus: OrderStatus) => {
    try {
      await updateStatus.mutateAsync({ orderId: order.id, status: newStatus });
      toast({ title: 'Status uppdaterad', description: 'Orderns status har ändrats.' });
    } catch (error) {
      toast({ title: 'Kunde inte uppdatera', description: 'Något gick fel. Försök igen.', variant: 'destructive' });
    }
  };

  const handleCancel = async () => {
    await handleStatusChange('cancelled');
    setCancelDialogOpen(false);
  };

  const handleCompleted = async () => {
    await handleStatusChange('completed');
    setCompletedDialogOpen(false);
  };

  const handleMarkPaid = async () => {
    try {
      await markPaid.mutateAsync({ orderId: order.id, method: selectedPaymentMethod });
      toast({
        title: 'Order markerad som betald',
        description: `Betalmetod: ${PAYMENT_METHODS.find(m => m.value === selectedPaymentMethod)?.label || selectedPaymentMethod}`,
      });
      setPaymentDialogOpen(false);
    } catch (error) {
      toast({ title: 'Kunde inte uppdatera', description: 'Något gick fel. Försök igen.', variant: 'destructive' });
    }
  };

  const handleUndoPaid = async () => {
    try {
      await markPaid.mutateAsync({ orderId: order.id, method: '', undo: true });
      toast({ title: 'Betalning ångrad', description: 'Ordern är inte längre markerad som betald.' });
    } catch (error) {
      console.error('[OrderDetailSheet] undo paid error:', error);
      toast({ title: 'Kunde inte ångra', description: 'Något gick fel. Försök igen.', variant: 'destructive' });
    }
  };

  const togglePicked = (index: number) => {
    const newSet = new Set(pickedItems);
    if (newSet.has(index)) newSet.delete(index);
    else newSet.add(index);
    setPickedItems(newSet);
  };

  const handleExportCsv = () => {
    const csv = generateOrderCsv(order, lineItems);
    downloadCsv(csv, `order-${order.order_number || order.id.slice(0, 8)}.csv`);
    toast({ title: 'CSV exporterad', description: 'Filen har laddats ner.' });
  };

  const handlePrint = () => printPickList(order, lineItems);

  const canAccept = currentStatus === 'new';
  const canMarkReady = currentStatus === 'in_progress';
  const canMarkCompleted = currentStatus === 'ready';
  const canCancel = currentStatus !== 'cancelled' && currentStatus !== 'completed';

  const completionLabel = order.delivery_type === 'delivery' ? 'Markera som levererad' : 'Markera som utlämnad';
  const completionDialogTitle = order.delivery_type === 'delivery' ? 'Bekräfta leverans' : 'Bekräfta utlämning';

  return (
    <>
    <ResponsiveOverlay
      open={open}
      onOpenChange={onOpenChange}
      variant="sidepanel"
      title={`Order #${order.order_number || order.id.slice(0, 8)}`}
    >
        <div className="flex items-center gap-3 mb-1" data-testid="order-detail-sheet">
          <h2 className="text-lg font-semibold">
            Order #{order.order_number || order.id.slice(0, 8)}
          </h2>
          <div data-testid="order-detail-status" data-status={order.status}>
            <OrderStatusBadge status={order.status} />
          </div>
        </div>
        <p className="flex items-center gap-4 text-sm text-muted-foreground mb-6">
          <span>{format(new Date(order.created_at), 'PPP HH:mm', { locale: sv })}</span>
          <span className="font-semibold text-foreground">
            {formatOrderPriceWhole(order.total_amount, order.currency)}
          </span>
        </p>

        <div className="mt-6 space-y-6">
          {/* Customer info */}
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-foreground">Kund</h4>
            {order.customer_name || order.customer_email || order.customer_phone ? (
              <div className="space-y-2 text-sm">
                {order.customer_name && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <User className="h-4 w-4" />
                    <span>{order.customer_name}</span>
                  </div>
                )}
                {order.customer_email && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Mail className="h-4 w-4" />
                    <a
                      href={`mailto:${order.customer_email}`}
                      className="underline underline-offset-2 hover:text-foreground transition-colors"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {order.customer_email}
                    </a>
                  </div>
                )}
                {order.customer_phone && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Phone className="h-4 w-4" />
                    <span>{order.customer_phone}</span>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Gäst</p>
            )}
          </div>

          <Separator />

          {/* Order details */}
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-foreground">Orderdetaljer</h4>
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Calendar className="h-4 w-4" />
                <span>{format(new Date(order.created_at), 'PPp', { locale: sv })}</span>
              </div>
              <div className="flex items-center gap-2 text-muted-foreground">
                <CreditCard className="h-4 w-4" />
                {isPaid ? (
                  <span className="text-green-700 font-medium">
                    ✓ Betald
                    {order.paid_method && (
                      <span className="text-muted-foreground font-normal ml-1 text-xs">
                        – {order.paid_method}
                      </span>
                    )}
                    {order.paid_at && (
                      <span className="text-muted-foreground font-normal ml-1 text-xs">
                        ({format(new Date(order.paid_at), 'PPp', { locale: sv })})
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={handleUndoPaid}
                      disabled={isMarkingPaid}
                      className="ml-2 text-xs text-muted-foreground underline hover:text-foreground print:hidden"
                    >
                      Ångra
                    </button>
                  </span>
                ) : (
                  <span>
                    {order.delivery_type === 'pickup' ? 'Betalas vid upphämtning' : 'Betalas enligt överenskommelse'}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 text-muted-foreground">
                {order.delivery_type === 'delivery' ? (
                  <>
                    <Truck className="h-4 w-4" />
                    <span>Leverans</span>
                  </>
                ) : (
                  <>
                    <MapPin className="h-4 w-4" />
                    <span>Upphämtning</span>
                  </>
                )}
              </div>
              {/* Pickup window info */}
              {(order as any).pickup_window_id && pickupWindowLabel && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Clock className="h-4 w-4" />
                  <span>{pickupWindowLabel}</span>
                </div>
              )}
            </div>
          </div>

          {/* Self-pickup instructions – shown when order is ready and window has instructions */}
          {currentStatus === 'ready' && pickupWindowData?.pickup_instructions && (
            <>
              <Separator />
              <div className="rounded-md border border-emerald-200 bg-emerald-50 dark:bg-emerald-900/20 dark:border-emerald-800 px-3 py-3 space-y-1">
                <p className="text-sm font-medium text-emerald-800 dark:text-emerald-200">
                  📍 Upphämtningsinstruktioner
                </p>
                <p className="text-sm text-emerald-700 dark:text-emerald-300">
                  {pickupWindowData.pickup_instructions}
                </p>
              </div>
            </>
          )}

          {order.delivery_type === 'delivery' && order.delivery_address && (
            <>
              <Separator />
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-foreground">Leveransadress</h4>
                <div className="p-3 rounded-lg bg-muted/50 text-sm">
                  <p>{order.delivery_address.street}</p>
                  <p>{order.delivery_address.postal_code} {order.delivery_address.city}</p>
                </div>
              </div>
            </>
          )}

          <Separator />

          {/* Pick list with product images */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium text-foreground">Plocklista</h4>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" onClick={handlePrint}>
                  <Printer className="h-4 w-4 mr-1" />
                  Skriv ut
                </Button>
                <Button size="sm" variant="outline" onClick={handleExportCsv}>
                  <Download className="h-4 w-4 mr-1" />
                  CSV
                </Button>
              </div>
            </div>
            
            {lineItems.length > 0 ? (
              <div className="space-y-2">
                {lineItems.map((item, idx) => (
                  <div 
                    key={item.id || idx} 
                    className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                      pickedItems.has(idx) ? 'bg-green-50 border-green-200' : 'bg-muted/50'
                    }`}
                  >
                    <Checkbox
                      checked={pickedItems.has(idx)}
                      onCheckedChange={() => togglePicked(idx)}
                    />
                    <ProductThumb
                      src={item.product_id ? coverMap[item.product_id] : undefined}
                      title={item.title}
                      size="sm"
                    />
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium ${pickedItems.has(idx) ? 'line-through text-muted-foreground' : ''}`}>
                        {item.title}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {item.quantity} × {formatOrderPriceWhole(item.unit_amount, order.currency)}
                      </p>
                    </div>
                    <div className="text-2xl font-bold text-primary shrink-0">
                      {item.quantity}
                    </div>
                    <div className="text-sm text-muted-foreground w-20 text-right shrink-0">
                      {formatOrderPriceWhole(item.total_amount, order.currency)}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Inga artiklar tillgängliga</p>
            )}
          </div>

          <Separator />

          {/* Totals */}
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Delsumma</span>
              <span>{formatOrderPriceWhole(order.subtotal_amount, order.currency)}</span>
            </div>
            {order.shipping_fee_amount > 0 && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Avgift</span>
                <span>{formatOrderPriceWhole(order.shipping_fee_amount, order.currency)}</span>
              </div>
            )}
            <div className="flex items-center justify-between font-medium">
              <span>Totalt</span>
              <span className="text-xl font-bold">
                {formatOrderPriceWhole(order.total_amount, order.currency)}
              </span>
            </div>
          </div>

          <Separator />

          <div className="rounded-md bg-muted/50 border p-3">
            <p className="text-sm text-muted-foreground">
              📦 Orderstatus hanteras i Farmly. Betalning hanteras separat via manuell markering eller betalsystem.
            </p>
          </div>

          {/* Status Actions – primary logistics first, payment second */}
          <div className="space-y-4">
            <h4 className="text-sm font-medium text-foreground">Åtgärder</h4>
            <div className="space-y-4">

              {/* 1. Accept / start packing – PRIMARY when new */}
              {canAccept && (
                <div className="space-y-1">
                  <Button onClick={() => handleStatusChange('in_progress')} disabled={isUpdating} data-testid="order-action-begin-packing">
                    {isUpdating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <PlayCircle className="h-4 w-4 mr-2" />}
                    Börja packa
                  </Button>
                  <p className="text-xs text-muted-foreground">
                    Markerar ordern som påbörjad. Kunden får ingen automatisk notis i v1.
                  </p>
                </div>
              )}

              {/* 2. Mark as paid – secondary */}
              {!isPaid && canCancel && (
                <div className="space-y-1">
                  <Button variant="outline" onClick={() => { setSelectedPaymentMethod('swish'); setPaymentDialogOpen(true); }} disabled={isMarkingPaid} data-testid="order-action-mark-paid">
                    {isMarkingPaid ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <HandCoins className="h-4 w-4 mr-2" />}
                    Markera som betald
                  </Button>
                  <p className="text-xs text-muted-foreground">
                    Använd när kunden betalar med Swish/kontant/faktura.
                  </p>
                </div>
              )}

              {/* 3. Mark ready */}
              {canMarkReady && (
                <div className="space-y-1">
                  <Button onClick={() => handleStatusChange('ready')} disabled={isUpdating} data-testid="order-action-mark-ready">
                    {isUpdating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Package className="h-4 w-4 mr-2" />}
                    Klar för upphämtning
                  </Button>
                  <p className="text-xs text-muted-foreground">
                    Ordern är packad och redo att hämtas eller skickas.
                  </p>
                </div>
              )}

              {/* 4. Mark completed */}
              {canMarkCompleted && (
                <div className="space-y-1">
                  <AlertDialog open={completedDialogOpen} onOpenChange={setCompletedDialogOpen}>
                    <AlertDialogTrigger asChild>
                      <Button disabled={isUpdating} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="order-action-mark-completed">
                        {isUpdating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}
                        {completionLabel}
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>{completionDialogTitle}</AlertDialogTitle>
                        <AlertDialogDescription>Bekräfta att kunden har fått sina varor. Detta avslutar ordern.</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Avbryt</AlertDialogCancel>
                        <AlertDialogAction onClick={handleCompleted} disabled={isUpdating} data-testid="order-action-confirm-completed">
                          {isUpdating && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                          Ja, avsluta ordern
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                  <p className="text-xs text-muted-foreground">Kunden har fått sina varor. Ordern markeras som avslutad.</p>
                </div>
              )}

              {/* 5. Contact customer */}
              <div className="space-y-1">
                <TooltipProvider>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline">
                        <PhoneCall className="h-4 w-4 mr-2" />
                        Kontakta kund
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start">
                      {order.customer_email ? (
                        <DropdownMenuItem asChild>
                          <a href={`mailto:${order.customer_email}`}><MailIcon className="h-4 w-4 mr-2" />E-post</a>
                        </DropdownMenuItem>
                      ) : (
                        <Tooltip><TooltipTrigger asChild><DropdownMenuItem disabled><MailIcon className="h-4 w-4 mr-2" />E-post</DropdownMenuItem></TooltipTrigger><TooltipContent>E-post saknas</TooltipContent></Tooltip>
                      )}
                      {order.customer_phone ? (
                        <DropdownMenuItem asChild>
                          <a href={`tel:${order.customer_phone}`}><Phone className="h-4 w-4 mr-2" />Ring</a>
                        </DropdownMenuItem>
                      ) : (
                        <Tooltip><TooltipTrigger asChild><DropdownMenuItem disabled><Phone className="h-4 w-4 mr-2" />Ring</DropdownMenuItem></TooltipTrigger><TooltipContent>Telefon saknas</TooltipContent></Tooltip>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TooltipProvider>
              </div>

              {/* 6. Cancel order */}
              {canCancel && (
                <div className="space-y-1">
                  <AlertDialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
                    <AlertDialogTrigger asChild>
                      <Button variant="destructive" disabled={isUpdating} data-testid="order-action-cancel">
                        <XCircle className="h-4 w-4 mr-2" />
                        Avbryt order
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Avbryt order?</AlertDialogTitle>
                        <AlertDialogDescription className="space-y-3">
                          <p>Ordern markeras som avbruten och tas bort från dina aktiva ordrar.</p>
                          {isPaid && (
                            <div className="rounded-md bg-amber-50 border border-amber-200 p-3">
                              <p className="text-amber-800 text-sm font-medium">⚠️ Ingen automatisk återbetalning</p>
                              <p className="text-amber-700 text-sm mt-1">Eventuell återbetalning måste hanteras manuellt i ditt betalningssystem.</p>
                            </div>
                          )}
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Avbryt</AlertDialogCancel>
                        <AlertDialogAction onClick={handleCancel} disabled={isUpdating} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="order-action-confirm-cancel">
                          {isUpdating && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                          Ja, avbryt ordern
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                  <p className="text-xs text-muted-foreground">Markerar ordern som avbruten. Kunden behöver kontaktas manuellt.</p>
                </div>
              )}
              
              {isPaid && (
                <div className="space-y-1 pt-2">
                  <Button variant="outline" disabled className="opacity-50">
                    <Banknote className="h-4 w-4 mr-2" />
                    Återbetala
                  </Button>
                  <p className="text-xs text-muted-foreground">Återbetalning hanteras i ditt betalningssystem.</p>
                </div>
              )}
            </div>
          </div>
        </div>
    </ResponsiveOverlay>

      <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Markera som betald</DialogTitle>
            <DialogDescription>Välj betalmetod som kunden använde.</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Select value={selectedPaymentMethod} onValueChange={setSelectedPaymentMethod}>
              <SelectTrigger><SelectValue placeholder="Välj betalmetod" /></SelectTrigger>
              <SelectContent>
                {PAYMENT_METHODS.map(m => (
                  <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPaymentDialogOpen(false)}>Avbryt</Button>
            <Button onClick={handleMarkPaid} disabled={isMarkingPaid} data-testid="order-action-confirm-paid">
              {isMarkingPaid && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Bekräfta betalning
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
